import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import { branding } from '@/config/branding';
import adminEn from '@/locales/en/admin.json';
import anatelEn from '@/locales/en/anatel.json';
import aneelEn from '@/locales/en/aneel.json';
import ansEn from '@/locales/en/ans.json';
import ibamaEn from '@/locales/en/ibama.json';
import bacenEn from '@/locales/en/bacen.json';
import cvmEn from '@/locales/en/cvm.json';
import cadeEn from '@/locales/en/cade.json';
import commonEn from '@/locales/en/common.json';
import adminEs from '@/locales/es/admin.json';
import anatelEs from '@/locales/es/anatel.json';
import aneelEs from '@/locales/es/aneel.json';
import ansEs from '@/locales/es/ans.json';
import ibamaEs from '@/locales/es/ibama.json';
import bacenEs from '@/locales/es/bacen.json';
import cvmEs from '@/locales/es/cvm.json';
import cadeEs from '@/locales/es/cade.json';
import commonEs from '@/locales/es/common.json';
import adminPt from '@/locales/pt/admin.json';
import anatelPt from '@/locales/pt/anatel.json';
import aneelPt from '@/locales/pt/aneel.json';
import ansPt from '@/locales/pt/ans.json';
import ibamaPt from '@/locales/pt/ibama.json';
import bacenPt from '@/locales/pt/bacen.json';
import cvmPt from '@/locales/pt/cvm.json';
import cadePt from '@/locales/pt/cade.json';
import commonPt from '@/locales/pt/common.json';

/**
 * English is the SOURCE language: add a key to `en` first, then pt and es.
 *
 * The default at runtime is Portuguese, because the users are Brazilian
 * regulatory lawyers and the corpora are Portuguese. Those two facts are not in
 * conflict -- one is about where keys are authored, the other about what a user
 * sees on first load.
 */
export const SUPPORTED_LOCALES = ['pt', 'en', 'es'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

const LOCALE_KEY = 'agencias.locale';

function initialLocale(): Locale {
  try {
    const stored = window.localStorage.getItem(LOCALE_KEY);
    if (stored && (SUPPORTED_LOCALES as readonly string[]).includes(stored)) {
      return stored as Locale;
    }
  } catch {
    /* fall through to the branding default */
  }
  return branding.defaultLocale;
}

void i18n.use(initReactI18next).init({
  resources: {
    en: {
      common: commonEn,
      admin: adminEn,
      anatel: anatelEn,
      aneel: aneelEn,
      ans: ansEn,
      ibama: ibamaEn,
      bacen: bacenEn,
      cvm: cvmEn,
      cade: cadeEn,
    },
    pt: {
      common: commonPt,
      admin: adminPt,
      anatel: anatelPt,
      aneel: aneelPt,
      ans: ansPt,
      ibama: ibamaPt,
      bacen: bacenPt,
      cvm: cvmPt,
      cade: cadePt,
    },
    es: {
      common: commonEs,
      admin: adminEs,
      anatel: anatelEs,
      aneel: aneelEs,
      ans: ansEs,
      ibama: ibamaEs,
      bacen: bacenEs,
      cvm: cvmEs,
      cade: cadeEs,
    },
  },
  lng: initialLocale(),
  fallbackLng: 'en',
  defaultNS: 'common',
  ns: ['common', 'anatel'],
  interpolation: {
    escapeValue: false,
    // `{{count, number}}` renders through Intl in the ACTIVE language, so 11282
    // reads as 11,282 in English and 11.282 in Portuguese. The backend sends the
    // raw measurement and never a formatted string: it does not know the
    // reader's locale, and a number formatted for the wrong one is worse than an
    // unformatted one -- "11.282" means eleven thousand in pt and eleven in en.
    format: (value, format, lng) =>
      format === 'number' && typeof value === 'number'
        ? new Intl.NumberFormat(lng).format(value)
        : String(value),
  },
});

i18n.on('languageChanged', (lng) => {
  try {
    window.localStorage.setItem(LOCALE_KEY, lng);
  } catch {
    /* non-fatal */
  }
});

export default i18n;
